<template>
  <div class="profile">
    <ShowTourButton @handleClick="$refs.pageTourRef.handleOpen()" />
    <h4 class="dashboard-pages__title">Profile</h4>

    <b-card body-class="p-0">
      <b-row class="p-3">
        <b-col sm="12">
          <ProfileBasicDetails />
        </b-col>
      </b-row>

      <b-tabs
        ref="profileTabs"
        v-if="personalProfile"
        card
        v-model="activeTabIndex"
        active-nav-item-class="profile-tab__active"
        nav-wrapper-class="profile-tab__navbar professional-information__tour"
        @input="handleProfileTabChange"
      >
        <b-tab title="Professional Information & Qualifications">
          <b-row>
            <b-col sm="12" xl="6">
              <b-row>
                <b-col md="12">
                  <CustomInput
                    type="text"
                    @updateVal="(val) => (personalProfile.profession = val)"
                    label="Profession"
                    :incomingValue="personalProfile.profession"
                    :wordCount="$charCount(personalProfile.profession)"
                    maxChars="200"
                  />
                </b-col>
                <b-col md="12" v-show="!branding || (Array.isArray(branding) && branding.length === 0)">
                  <CustomInput
                    type="text"
                    @updateVal="(val) => (personalProfile.employer = val)"
                    label="Employer"
                    :incomingValue="personalProfile.employer"
                    :wordCount="$charCount(personalProfile.employer)"
                    maxChars="200"
                  />
                </b-col>
                <b-col md="12">
                  <CustomInput
                    type="text"
                    @updateVal="
                      (val) => (personalProfile.businessWebsiteAssociated = val)
                    "
                    label="Business or Website Associated"
                    :incomingValue="personalProfile.businessWebsiteAssociated"
                    :wordCount="
                      $charCount(personalProfile.businessWebsiteAssociated)
                    "
                    maxChars="200"
                  />
                </b-col>
                <b-col md="12" v-show="!branding || (Array.isArray(branding) && branding.length === 0)">
                  <CustomInput
                    type="text"
                    @updateVal="
                      (val) => (personalProfile.registrationNumber = val)
                    "
                    label="Professional Registration Reference/Number"
                    :incomingValue="personalProfile.registrationNumber"
                    :wordCount="$charCount(personalProfile.registrationNumber)"
                    maxChars="200"
                  />
                </b-col>
                <b-col md="12">
                  <b-row>
                    <b-col md="12">
                      <CustomInput
                        type="number"
                        @updateVal="
                          (val) => (personalProfile.hoursPerYear = val)
                        "
                        label="Required CPD Hours per Reporting Period"
                        :incomingValue="displayPersonalProfile ? displayPersonalProfile.hoursPerYear : personalProfile.hoursPerYear"
                        :wordCount="
                          $charCount(personalProfile.registrationNumber)
                        "
                        :disabled="organisationSettings && organisationSettings.cpdHours !== null"
                      />
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col md="6">
                      <CustomInput
                        type="date"
                        label="Start Date"
                        :incomingValue="displayPersonalProfile ? displayPersonalProfile.startDate : personalProfile.startDate"
                        @updateVal="(val) => (personalProfile.startDate = val)"
                        :disabled="organisationSettings && organisationSettings.startDate !== null"
                      />
                    </b-col>
                    <b-col md="6">
                      <CustomInput
                        type="select"
                        label="Report Interval"
                        :incomingValue="displayPersonalProfile ? displayPersonalProfile.reportInterval : personalProfile.reportInterval"
                        @updateVal="
                          (val) => (personalProfile.reportInterval = val)
                        "
                        :options="reportIntervalOptions"
                        :disabled="organisationSettings && organisationSettings.reportInterval !== null"
                      />
                    </b-col>
                  </b-row>
                </b-col>
                <b-col md="12">
                  <CustomInput
                    @updateVal="(val) => (personalProfile.qualifications = val)"
                    label="Qualifications"
                    :incomingValue="personalProfile.qualifications"
                    :wordCount="$wordCount(personalProfile.qualifications)"
                    maxChars="500"
                    :multipleRows="true"
                  />
                </b-col>
                <b-col md="12">
                  <p class="label-title">Signature</p>
                  <SignaturePad
                    :modelValue="personalProfile.signature"
                    :locked="profileSignatureLocked"
                    locked-message="Uploaded images cannot be edited. Clear the signature to draw a new one."
                    @update:modelValue="handleSignatureChange"
                  >
                    <input
                      ref="signatureUploadInput"
                      type="file"
                      accept="image/png,image/jpeg"
                      @change="handleSignatureUpload"
                      style="display: none"
                    >
                    <small class="signature-upload-note">
                      PNG or JPEG, max 40KB
                    </small>
                    <b-button
                      variant="outline-dark"
                      class="ovr-button__inverted"
                      @click="chooseSignatureFile"
                    >
                      Upload
                    </b-button>
                  </SignaturePad>
                </b-col>
              </b-row>
            </b-col>
            <b-col sm="12" xl="6">
              <b-card body-class="governing-bodies__card governing-body__tour">
                <ProfileGoverningBody
                  ref="governingBodyComponent"
                  @updated-governing-bodies="governingBodiesUpdated"
                />
                <hr />
                <b-form-group label="Interests" v-show="!branding || (Array.isArray(branding) && branding.length === 0)">
                  <b-form-checkbox-group
                    class="ovr interests-checkboxes"
                    v-model="personalProfile.interests"
                    :options="interestOptions"
                    switches
                  />
                </b-form-group>
                <hr />
                <TagsManager 
                    title="Entry tags"
                    class="mt-2 profile-tag__entry-tour"
                    provider="profile"
                />
              </b-card>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab
          title="Additional Information"
          v-if="getFeatures.includes('ProfileTabAdditionalInformation')"
        >
          <CustomInput
            @updateVal="(val) => (personalProfile.summaryOfWork = val)"
            label="Summary of Recent Work"
            :incomingValue="personalProfile.summaryOfWork"
            :wordCount="$wordCount(personalProfile.summaryOfWork)"
            maxChars="500"
          />
          <CustomInput
            @updateVal="(val) => (personalProfile.jobDescription = val)"
            label="Job Description"
            :incomingValue="personalProfile.jobDescription"
            :wordCount="$wordCount(personalProfile.jobDescription)"
            maxChars="500"
          />
          <CustomInput
            @updateVal="(val) => (personalProfile.personalStatement = val)"
            label="Personal Statement"
            :incomingValue="personalProfile.personalStatement"
            :wordCount="$wordCount(personalProfile.personalStatement)"
            maxChars="1500"
          />
        </b-tab>
        <b-tab title="HCPC Information" v-if="governingBodies.includes('HCPC')">
          <CustomInput
            @updateVal="(val) => (personalProfile.hcpcStandard1 = val)"
            label="Meeting HCPC Standard 1"
            :incomingValue="personalProfile.hcpcStandard1"
            :wordCount="$wordCount(personalProfile.hcpcStandard1)"
            maxChars="500"
          />
          <CustomInput
            @updateVal="(val) => (personalProfile.hcpcStandard2 = val)"
            label="Meeting HCPC Standard 2"
            :incomingValue="personalProfile.hcpcStandard2"
            :wordCount="$wordCount(personalProfile.hcpcStandard2)"
            maxChars="500"
          />
          <CustomInput
            @updateVal="(val) => (personalProfile.hcpcStandard3 = val)"
            label="Meeting HCPC Standard 3"
            :incomingValue="personalProfile.hcpcStandard3"
            :wordCount="$wordCount(personalProfile.hcpcStandard3)"
            maxChars="500"
          />
          <CustomInput
            @updateVal="(val) => (personalProfile.hcpcStandard4 = val)"
            label="Meeting HCPC Standard 4"
            :incomingValue="personalProfile.hcpcStandard4"
            :wordCount="$wordCount(personalProfile.hcpcStandard4)"
            maxChars="500"
          />
        </b-tab>
        <b-tab title="Reviewer Contacts">
          <ReviewerContactsManager />
        </b-tab>
        <b-tab title="Personal Documents">
          <b-row>
            <b-col sm="12">
              <ProfileDocumentUpload
                :personalProfile="personalProfile"
                @document-uploaded="handleDocumentUploaded"
              />
            </b-col>
            <b-col sm="12" class="mt-3">
              <DocumentPreview
                :personalProfile="personalProfile"
                ref="documentPreview"
                :key="documentPreviewKey"
                @document-deleted="handleDocumentDeleted"
              />
            </b-col>
          </b-row>
        </b-tab>

        <b-tab
          v-for="group in additionalFieldGroups"
          v-bind:key="group.title"
          :title="group.title"
        >
          <b-form-group
            v-for="fieldDefinition in group.fields"
            v-bind:key="fieldDefinition.fieldId"
            :label="fieldDefinition.title"
          >
            <b-textarea
              rows="5"
              v-model="additionalFieldValues[fieldDefinition.fieldId]"
              v-if="fieldDefinition.type === 'LongText'"
            >
            </b-textarea>
            <b-input
              type="text"
              v-model="additionalFieldValues[fieldDefinition.fieldId]"
              v-if="fieldDefinition.type === 'ShortText'"
            />
            <b-form-select
              v-model="additionalFieldValues[fieldDefinition.fieldId]"
              :options="fieldDefinition.values"
              v-if="fieldDefinition.type === 'Dropdown'"
            >
            </b-form-select>
            <ListProfileField
              :field="fieldDefinition"
              v-model="additionalFieldValues[fieldDefinition.fieldId]"
              v-if="fieldDefinition.type === 'List'"
            />

            <p
              style="font-size: 12px"
              class="mt-2"
              v-if="fieldDefinition.help"
              v-html="formatHelpText(fieldDefinition.help)"
            ></p>
          </b-form-group>
        </b-tab>

        <b-tab
          title="Annual Reviews"
          v-if="getFeatures.includes('OrganisationUserReview')"
        >
          <AnnualReviews />
        </b-tab>
      </b-tabs>

      <div class="actions mt-3" v-if="showProfileActions">
        <b-col sm="12" lg="12" class="actions-right">
          <b-button
            variant="dark"
            class="mb-0 mt-2 ovr-button__inverted actions-save__close-profile button-continue__profile actions-save__close actions-save__continue-profile"
            v-b-tooltip.hover
            title="Save current work and continue editing"
            @click="saveProfile(false)"
            >Save and Continue</b-button
          >
          <b-button
            @click="saveAndClose"
            class="mt-2 ovr-button__inverted actions-save__close button-close__profile actions-save__close-profile"
            v-b-tooltip.hover
            title="Save current work and exit"
            variant="dark"
            >Save and Close</b-button
          >
        </b-col>
      </div>
    </b-card>

    <b-card
      no-body
      class="mb-4"
      v-if="!loading && personalProfile"
      @change="profileChanged()"
    >
    </b-card>

    <PageTour
      ref="pageTourRef"
      description="Here is where you keep all your information like professional qualifications and information that generally you might find in your CV like a summary of recent work, your current job role, and important sections that relate to you as a professional or student. <br> <br> Ok, let's tour:"
      @handleShowTour="handleShowTour"
    />
    <v-tour
      name="profileTour"
      :steps="steps"
      :options="{ highlight: true }"
    ></v-tour>
    <Pulse loading v-if="loading" class="loader" color="#999999" />

    <AutosaveModal
      ref="autosaveModal"
      @leave-without-saving="leaveRoute"
      @save-and-exit="saveAndClose"
    />
  </div>
</template>

<script>
import Pulse from "../../components/loaders/Pulse";
import ProfileBasicDetails from "../../components/profile/ProfileBasicDetails";
import ProfileGoverningBody from "../../components/profile/ProfileGoverningBody";
import AutosaveModal from "../../components/entries/AutosaveModal";
import ContentSaveIcon from "vue-material-design-icons/ContentSave";
import ContentSaveEditIcon from "vue-material-design-icons/ContentSaveEdit";
import EventBus from "../../event-bus";
import ListProfileField from "../../components/profile/ListProfileField";
import { mapGetters } from "vuex";
import RichTextEditor from "../../components/common/RichTextEditor";
import SignaturePad from "../../components/form/SignaturePad";
import AnnualReviews from "../../components/profile/AnnualReviews";
import CustomInput from "../../components/common/CustomInput";
import TagsManager from "@/components/common/TagsManager";
import ProfileDocumentUpload from "../../components/profile/ProfileDocumentUpload";
import DocumentPreview from "../../components/profile/DocumentPreview";
import ReviewerContactsManager from "../../components/profile/ReviewerContactsManager";
import PageTour from "../../components/common/PageTour";
import ShowTourButton from "../../components/common/ShowTourButton";
import { REPORT_INTERVAL_OPTIONS } from "@/constants/reportIntervals";
import Providers from "@/providers";

export default {
  name: "Profile",
  components: {
    TagsManager,
    ReviewerContactsManager,
    ProfileDocumentUpload,
    DocumentPreview,
    AnnualReviews,
    RichTextEditor,
    CustomInput,
    SignaturePad,
    PageTour,
    ShowTourButton,
    ListProfileField,
    AutosaveModal,
    ProfileGoverningBody,
    ProfileBasicDetails,
    Pulse,
    ContentSaveIcon,
    ContentSaveEditIcon,
  },
  watch: {
    branding(newVal) {
      console.log('[Profile] Branding changed:', newVal);
      console.log('[Profile] Employer field should be visible:', newVal == null);
    }
  },
  computed: {
    ...mapGetters("features", ["getFeatures"]),
    displayPersonalProfile() {
      if (!this.personalProfile) return null;
      
      // Create a copy of personal profile
      const profile = { ...this.personalProfile };
      
      // Override with organization settings if they exist
      if (this.organisationSettings) {
        if (this.organisationSettings.cpdHours !== null) {
          profile.hoursPerYear = this.organisationSettings.cpdHours;
        }
        if (this.organisationSettings.reportInterval !== null) {
          profile.reportInterval = this.organisationSettings.reportInterval;
        }
        if (this.organisationSettings.startDate !== null) {
          profile.startDate = this.organisationSettings.startDate;
        }
      }
      
      return profile;
    }
  },
  data: function () {
    return {
      personalProfile: null,
      profileImage: null,
      loading: false,
      edited: false,
      continueRoute: null,
      governingBodies: [],
      interestOptions: [
        "Advanced Practice",
        "Course Provider",
        "Education",
        "Educational Support",
        "Emergency Medicine",
        "Lecturing",
        "Mental Heath",
        "Mentorship",
        "Practice Education",
        "Preceptorship",
        "Safeguarding",
        "Specialist Field",
        "Voluntary Service",
      ],
      reportIntervalOptions: REPORT_INTERVAL_OPTIONS,
      activeTabIndex: 0,
      showProfileActions: true,
      additionalFieldGroups: [],
      additionalFieldValues: [],
      profileSignatureLocked: false,
      branding: null,
      documentPreviewKey: 0,
      organisationSettings: null,
      userOrganisationId: null,
      steps: [
        {
          target: ".basic-details__tour",
          content: `Here you can find your personal and professional information. You can also add an avatar or image of yourself to your profile. <br> In this section you can also see your membership status, manage your subscription and see when your membership is due. <br> <br> If you need help, remember that you can contact our support team at any time.`,
        },
        {
          target: ".professional-information__tour",
          content: `In these sections you can put information that you might need to pull into your professional portfolio such as your profession and qualifications. Additional information could include a summary of recent work, your job description, and a personal statement.`,
        },
        {
          target: ".governing-body__tour",
          content: `When you registered, you may have selected the governing body you are regulated with.
                    <br><br> From here you can add or amend your governing body by clicking the edit option.`,
        },
        {
          target: ".profile-tag__entry-tour",
          content: `A great feature to personalise your CPD entries. <br><br> Click to create your CPD tags that you can link to your CPD Diary and Reflective Entries.`,
        },
        {
          target: ".button-continue__profile",
          content: `You can choose ‘Save and Continue’ if you are continuing to make changes and wish to save your progress so far.`,
          params: {
            placement: "top",
          },
        },
        {
          target: ".button-close__profile",
          content: `Here you can choose ‘Save and Close’ to save any changes and close the page.`,
          params: {
            placement: "top",
          },
        },
      ],
    };
  },
  methods: {
    handleProfileTabChange(tabIndex) {
      this.activeTabIndex = tabIndex;
      const tabs = this.$refs.profileTabs?.tabs || [];
      const activeTab = tabs[tabIndex];
      this.showProfileActions = activeTab?.title !== "Reviewer Contacts";
    },
    handleShowTour() {
      this.$tours["profileTour"].start();
    },
    loadPersonalProfile() {
      this.loading = true;
      this.$api.v2
        .get("personal-profile")
        .then((resp) => {
          this.personalProfile = resp.body;
          // Organization information is now included in the personal profile response
          if (this.personalProfile.organisation) {
            this.userOrganisationId = this.personalProfile.organisation.id;
            this.loadOrganizationSettings();
          }
        })
        .catch((resp) => {
          this.$httpError("Failed to load profile", resp);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    governingBodiesUpdated() {
      this.loadGoverningBodies();
      this.loadAdditionalFields();
      this.loadAdditionalFieldValues();
    },
    loadGoverningBodies() {
      this.loading = true;
      this.$api.v2
        .get("profile/governing-bodies")
        .then((resp) => {
          this.governingBodies = resp.body;
          this.loading = false;
        })
        .catch((err) => {
          this.$httpError("Failed to get governing bodies", err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadAdditionalFields() {
      this.$api.v2
        .get("personal-profile/additional-fields")
        .then((resp) => {
          this.additionalFieldGroups = resp.body;
        })
        .catch((resp) => {
          this.$httpError("Failed to load additional fields", resp);
        });
    },
    loadAdditionalFieldValues() {
      this.$api.v2
        .get("personal-profile/additional-fields/values")
        .then((resp) => {
          let newValue = [];
          let values = resp.body;
          for (let value of values) {
            newValue[value.fieldId] = value.value;
          }
          this.additionalFieldValues = newValue;
        })
        .catch((resp) => {
          this.$httpError("Failed to load additional field values", resp);
        });
    },
    profileChanged() {
      this.edited = true;
    },
    saveAdditionalFieldValues() {
      if (this.additionalFieldGroups.length > 0) {
        let data = [];
        for (let fieldId in this.additionalFieldValues) {
          data.push({
            fieldId: parseInt(fieldId),
            value: this.additionalFieldValues[fieldId],
          });
        }
        this.$api.v2
          .put("personal-profile/additional-fields/values", data)
          .then(() => {
            this.loadAdditionalFieldValues();
          })
          .catch((resp) => {
            this.$httpError("Failed to save additional fields", resp);
          });
      }
    },
    saveProfile(close = false) {
      this.loading = true;
      this.$api.v2
        .put("personal-profile", this.personalProfile)
        .then((resp) => {
          this.personalProfile = resp.body;
          this.loading = false;
          this.edited = false;
          this.saveAdditionalFieldValues();
          EventBus.$emit("show-toast", {
            title: "Saved Profile",
            message: "Your profile has been updated successfully.",
          });
          if (close) {
            this.$router.push({ name: "dashboard-home" });
          }
        })
        .catch((resp) => {
          this.$httpError("Failed to update profile", resp);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    leaveRoute() {
      if (this.continueRoute) {
        this.continueRoute();
      }
    },
    saveAndClose() {
      this.saveProfile(true);
    },
    loadBranding() {
      this.$api.v2
        .get("branding")
        .then((resp) => {
          this.branding = resp.body;
          console.log('[Profile] Branding loaded:', this.branding);
        })
        .catch((err) => {
          console.log('[Profile] Branding 404 or error:', err);
          console.log('[Profile] Branding value after error:', this.branding);
        });
    },
    handleDocumentUploaded(document) {
      // Force refresh the DocumentPreview component
      this.documentPreviewKey += 1;
    },
    handleDocumentDeleted(document) {
      // Document deletion is handled by the DocumentPreview component itself
      // This event is mainly for any additional parent-level handling if needed
      console.log('Document deleted:', document);
    },
    formatHelpText(text) {
      return text.replace(/(?:\r\n|\r|\n)/g, "<br>");
    },
    chooseSignatureFile() {
      this.$refs.signatureUploadInput.click();
    },
    handleSignatureChange(value) {
      this.personalProfile.signature = value;
      if (!value) {
        this.profileSignatureLocked = false;
      }
    },
    handleSignatureUpload(event) {
      const files = event.target.files || event.dataTransfer.files;
      if (!files || !files.length) {
        return;
      }

      const file = files[0];
      if (file.size > 40 * 1024) {
        alert("File size cannot exceed 40KB");
        event.target.value = "";
        return;
      }

      const reader = new FileReader();

      reader.onload = (loadEvent) => {
        const uploadedSignature = loadEvent.target?.result;
        if (typeof uploadedSignature !== "string") {
          return;
        }

        this.handleSignatureChange(
          uploadedSignature.replace(/^data:image\/[^;]+/, "data:image/octet-stream")
        );
        this.profileSignatureLocked = true;
      };

      reader.readAsDataURL(file);
      event.target.value = "";
    },
    loadOrganizationSettings() {
      if (!this.userOrganisationId) {
        return;
      }
      
      Providers.organisationManagement.getSettings(this.userOrganisationId).then((resp) => {
        this.organisationSettings = resp.body;
      }).catch((resp) => {
        // If we can't load settings, that's fine - just don't override anything
        console.log('Could not load organization settings');
      });
    },
  },
  created() {
    console.log('[Profile] Component created, initial branding:', this.branding);
    this.loadPersonalProfile();
    this.loadGoverningBodies();
    this.loadAdditionalFields();
    this.loadAdditionalFieldValues();
    this.loadBranding();
  },
  beforeRouteLeave(to, from, next) {
    if (this.edited) {
      this.$refs.autosaveModal.show();
      this.continueRoute = next;
    } else {
      next();
    }
  },
};
</script>

<style lang="scss">
.button-area {
  text-align: center;
}

.profile-tab__navbar {
  background-color: $blue-700 !important;
  border: none !important;

  .nav-link {
    border: none !important;
    color: $blue-900 !important;
    font-weight: 600;
    font-size: 14px;
  }
}

.profile-tab__active {
  background-color: white !important;
  border: none !important;
  border-radius: 8px 8px 0 0 !important;
}

.governing-bodies__card {
  .custom-switch {
    flex: 50%;
    margin-right: 0;
  }

  .interests-checkboxes {
    display: flex;
    flex-wrap: wrap;
  }
}

@media (max-width: 768px) {
  .governing-bodies__card {
    .custom-switch {
      flex: 100%;
    }
  }
}
</style>

<style lang="scss" scoped>
.governing-bodies__card {
  background-color: $background;
}

.actions {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-bottom: 24px;
}

.actions-left,
.actions-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.actions-save__close {
  justify-content: flex-end;
  display: flex;
  color: white;
  font-weight: 600;
  margin-left: 32px;
  background: $blue-300 !important;
  border-radius: 8px;
  padding: 0.375rem 22px;
}

.signature-upload-note {
  align-self: center;
  color: #6c757d;
}

@media screen and (max-width: 991px) {
  .actions-right,
  .actions-save__close-profile,
  .actions-save__continue-profile {
    justify-content: center;
  }
}

@media screen and (max-width: 768px) {
  .actions-left,
  .actions-save__close {
    justify-content: center;
    display: flex;
  }
}
</style>
